import React from "react";
import { useParams, Navigate } from "react-router-dom";
import { Link } from "@/components/LocaleLink";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Package, Target, ShieldCheck, Users, Wallet } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { getIndsatser, getProduct } from "@/data/indsatser";
import { useSeo } from "@/hooks/useSeo";
import IndsatsCTA from "@/components/IndsatsCTA";
import { ProductPdfButton } from "@/components/LazyProductPdf";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { Reveal } from "@/components/Reveal";

const IndsatsDetail = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const product = getProduct(lang, slug);
  const labels = getIndsatser(lang).labels;
  const guarantees = getIndsatser(lang).overview.guarantees;

  useSeo(
    product ? `${product.name} | Lotusgruppen` : "Lotusgruppen",
    product ? product.summary : ""
  );

  if (!product) return <Navigate to="/indsatser" replace />;

  const { Icon } = product;

  return (
    <>
      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden" style={{ backgroundColor: product.accent }} data-testid="indsats-detail-hero">
        <div className="absolute inset-0 lotus-grain opacity-[0.08]" aria-hidden="true" />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 text-white">
          <Link to="/indsatser" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors duration-300 mb-8" data-testid="back-to-indsatser">
            <ArrowLeft className="h-4 w-4" /> {labels.backToOverview}
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <div className="flex items-center gap-4 mb-5">
              <span className="flex items-center justify-center h-14 w-14 rounded-2xl bg-white/15 backdrop-blur-sm">
                <Icon className="h-7 w-7" strokeWidth={1.5} />
              </span>
              <span className="text-xs uppercase tracking-[0.25em] text-white/80">
                {product.flagship ? labels.flagship : labels.family}
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              {product.name}
              {product.subtitle && <span className="block text-2xl sm:text-3xl font-sans font-light text-white/85 mt-2">– {product.subtitle}</span>}
            </h1>
            <p className="mt-5 text-xl text-white/90 max-w-2xl">{product.tagline}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2.5 rounded-full bg-white/15 backdrop-blur-sm px-5 py-2.5 text-sm">
                <Clock className="h-4 w-4" /> <span className="uppercase tracking-widest text-white/70 text-xs">{labels.duration}</span> {product.duration}
              </div>
              <ProductPdfButton product={product} onDownload={() => trackEvent(EVENTS.PDF_DOWNLOAD, { slug: product.slug })} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro + meta */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-10 lg:gap-14 items-start">
          <Reveal>
            <p className="font-display text-2xl sm:text-3xl text-[#2D5A3F] leading-snug">{product.intro}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-4">
              <div className="rounded-2xl bg-[#EAE5D9] p-6">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5C7567] mb-2"><Users className="h-4 w-4" /> {labels.forWhom}</div>
                <p className="text-[#1A2E24] leading-relaxed">{product.forWhom}</p>
              </div>
              <div className="rounded-2xl border border-[#D3CEC0] p-6">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5C7567] mb-2"><Wallet className="h-4 w-4" /> {labels.price}</div>
                <p className="text-[#1A2E24] leading-relaxed">{product.priceNote}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Process / phases */}
      {product.process?.length > 0 && (
        <section className="bg-[#F7F5F0] border-y border-black/5" data-testid="indsats-process">
          <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
            <Reveal>
              <h2 className="font-display text-3xl sm:text-4xl text-[#1A2E24] mb-12">{labels.process}</h2>
            </Reveal>
            <ol className="relative border-l-2 border-[#D3CEC0] ml-3 space-y-8">
              {product.process.map((step, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="relative pl-8"
                  data-testid={`process-step-${i}`}
                >
                  <span
                    className="absolute -left-[11px] top-1 flex items-center justify-center h-5 w-5 rounded-full text-white text-[10px] font-bold"
                    style={{ backgroundColor: product.accent }}
                  >
                    {i + 1}
                  </span>
                  {step.phase && <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: product.accent }}>{step.phase}</span>}
                  <h3 className="font-display text-2xl text-[#1A2E24] mt-0.5">{step.title}</h3>
                  <p className="mt-1.5 text-[#3B5245] leading-relaxed max-w-2xl">{step.text}</p>
                </motion.li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* Deliverables + results */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24 grid md:grid-cols-2 gap-8 lg:gap-12">
        <Reveal>
          <div className="rounded-3xl bg-white border border-black/5 p-8 h-full">
            <div className="flex items-center gap-3 mb-6">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#EAE5D9] text-[#2D5A3F]"><Package className="h-5 w-5" /></span>
              <h2 className="font-display text-2xl sm:text-3xl text-[#1A2E24]">{labels.deliverables}</h2>
            </div>
            <ul className="space-y-3">
              {product.deliverables.map((it) => (
                <li key={it} className="flex items-start gap-3 text-[#3B5245]">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: product.accent }} /> {it}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="rounded-3xl bg-[#1A2E24] text-white p-8 h-full">
            <div className="flex items-center gap-3 mb-6">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-white/10 text-[#8EA694]"><Target className="h-5 w-5" /></span>
              <h2 className="font-display text-2xl sm:text-3xl">{labels.results}</h2>
            </div>
            <ul className="space-y-3">
              {product.results.map((it) => (
                <li key={it} className="flex items-start gap-3 text-[#EAE5D9]">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#8EA694] shrink-0" /> {it}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      {/* Guarantees strip */}
      <section className="bg-[#EAE5D9]" data-testid="indsats-guarantees">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <h2 className="font-display text-3xl sm:text-4xl text-[#1A2E24] mb-8">{labels.guarantees}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {guarantees.map((g) => (
              <div key={g} className="flex items-start gap-3 rounded-2xl bg-white border border-black/5 p-5">
                <ShieldCheck className="h-5 w-5 text-[#2D5A3F] shrink-0" />
                <span className="text-[#1A2E24] font-medium leading-snug">{g}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <IndsatsCTA dark slug={product.slug} />
    </>
  );
};

export default IndsatsDetail;
